import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Money,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { ARCHIVE_REQUEST_STATUSES, type ArchiveRequestStatus } from '@mlm/shared';
import {
  apiErrorMessage,
  executeArchiveRequest,
  getArchiveRequestPreflight,
  getTenantFeatures,
  listArchiveRequests,
  rejectArchiveRequest,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';
import { ReviewDecisionDialog } from '../components/review-decision-dialog';

const PAGE_SIZE = 25;

const STATUS_VARIANT: Record<ArchiveRequestStatus, 'warning' | 'muted' | 'success'> = {
  requested: 'warning',
  cancelled: 'muted',
  rejected: 'muted',
  completed: 'success',
};

/** Copy for the one condition that actually STOPS an archive. */
const BLOCKER_COPY: Record<string, string> = {
  archive_blocked_open_payout:
    'This member has an open cash-out. Pay it or reject it first — archiving would strand money the member is still owed.',
};

/**
 * Admin → Members → Archive requests. The queue behind a member asking to be
 * erased.
 *
 * The point of the pre-flight is that archival is IRREVERSIBLE and mostly
 * un-blockable: only an open cash-out refuses outright, and everything else on
 * the panel is judgement the operator has to exercise. So the numbers are
 * shown individually and un-summarised — there is no verdict for us to compute
 * on their behalf.
 */
export function ArchiveRequestsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ArchiveRequestStatus | 'all'>('requested');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const canDecide = useHasPerm('member.archive');
  // What Execute will DO here, so the page and the confirm dialog describe the
  // operation this tenant actually runs. Defaults to the reversible reading
  // while it loads: promising an erasure we do not perform is the worse error.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const erasing = featuresQ.data?.account_archive_mode === 'erase';

  const { data, isLoading } = useQuery({
    queryKey: ['admin-archive-requests', status, page],
    queryFn: () =>
      listArchiveRequests({
        status: status === 'all' ? undefined : status,
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  // Clearing the selection matters: the detail panel below is keyed on the id,
  // and a decided request leaves the default `requested` filter entirely.
  const afterDecision = () => {
    void qc.invalidateQueries({ queryKey: ['admin-archive-requests'] });
    void qc.invalidateQueries({ queryKey: ['users'] });
    setSelected(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Archive requests</h1>
        {/* Two different operations behind one button, so the page says which
            one this tenant runs. See Settings → Members. */}
        <p className="text-sm text-muted-foreground">
          {erasing
            ? 'Members who asked for their account to be erased. Archiving scrubs their personal details and cannot be undone — read the pre-flight before executing.'
            : 'Members who asked to close their account. Archiving signs them out and blocks sign-in; nothing is deleted, and the account can be restored. Read the pre-flight before executing.'}
        </p>
      </div>

      <Tabs
        value={status}
        onValueChange={(v) => {
          setStatus(v as ArchiveRequestStatus | 'all');
          setPage(1);
          setSelected(null);
        }}
      >
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          {ARCHIVE_REQUEST_STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} className="capitalize">
              {s}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No requests here.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Member</th>
                  <th className="p-3">Reason given</th>
                  <th className="p-3">Requested</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b last:border-0 ${selected === r.id ? 'bg-muted/40' : ''}`}
                  >
                    <td className="p-3">
                      <Link to={`/members/${r.userId}`} className="font-medium hover:underline">
                        {r.memberName ?? r.memberEmail}
                      </Link>
                      <div className="text-xs text-muted-foreground">{r.memberEmail}</div>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {r.reason || <span className="opacity-60">— none given —</span>}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {new Date(r.requestedAt).toLocaleString()}
                      {r.decidedAt && <div>decided {new Date(r.decidedAt).toLocaleString()}</div>}
                    </td>
                    <td className="p-3">
                      <Badge variant={STATUS_VARIANT[r.status] ?? 'muted'} className="capitalize">
                        {r.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        variant={selected === r.id ? 'default' : 'outline'}
                        onClick={() => setSelected(selected === r.id ? null : r.id)}
                      >
                        {selected === r.id ? 'Hide pre-flight' : 'Pre-flight'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} · {total} request{total === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {selected && <PreflightPanel id={selected} canDecide={canDecide} onDecided={afterDecision} />}
    </div>
  );
}

function PreflightPanel({
  id,
  canDecide,
  onDecided,
}: {
  id: string;
  canDecide: boolean;
  onDecided: () => void;
}) {
  const [executeOpen, setExecuteOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);
  // What Execute will DO here, so the page and the confirm dialog describe the
  // operation this tenant actually runs. Defaults to the reversible reading
  // while it loads: promising an erasure we do not perform is the worse error.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const erasing = featuresQ.data?.account_archive_mode === 'erase';

  const { data, isLoading } = useQuery({
    queryKey: ['admin-archive-preflight', id],
    // Never served from cache: the pre-flight is a snapshot of live money and
    // the operator is about to act on it irreversibly.
    staleTime: 0,
    queryFn: () => getArchiveRequestPreflight(id),
  });

  const execute = useMutation({
    mutationFn: () => executeArchiveRequest(id),
    // The API re-checks the cash-out blocker INSIDE its transaction, so a
    // request that passed pre-flight minutes ago can still 409. Those two land
    // inline in the confirm dialog instead of as a toast the operator can
    // dismiss without reading; everything else keeps the default toast.
    meta: {
      successMessage: 'Account archived.',
      silentErrorCodes: ['archive_blocked_open_payout', 'archive_request_not_open'],
    },
    onError: (err) => setExecuteError(apiErrorMessage(err, 'Could not archive this account.')),
    onSuccess: () => {
      setExecuteOpen(false);
      onDecided();
    },
  });

  const reject = useMutation({
    mutationFn: (note: string) => rejectArchiveRequest(id, note || undefined),
    meta: { successMessage: 'Request rejected.' },
    onSuccess: () => {
      setRejectOpen(false);
      onDecided();
    },
  });

  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Loading pre-flight…
        </CardContent>
      </Card>
    );
  }

  const { request, preflight } = data;
  const blocked = preflight.blockers.length > 0;
  const decidable = canDecide && request.status === 'requested';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Pre-flight — {request.memberName ?? request.memberEmail}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Wallet balance">
            <Money amount={preflight.walletBalance} direction="neutral" />
          </Stat>
          <Stat label="Open cash-outs">{preflight.openPayoutRequests}</Stat>
          <Stat label="Active slots">{preflight.activeSlots}</Stat>
          <Stat label="Active downline">{preflight.activeDownline}</Stat>
          <Stat label="Open orders">{preflight.openOrders}</Stat>
          <Stat label="Live enrollments">{preflight.liveEnrollments}</Stat>
        </div>

        {(preflight.isCreator || preflight.isMerchant) && (
          <div className="flex flex-wrap gap-2">
            {preflight.isCreator && <Badge variant="warning">Also a creator</Badge>}
            {preflight.isMerchant && <Badge variant="warning">Also a merchant</Badge>}
          </div>
        )}

        {/* The single most misread thing about this action. An operator who
            assumes archiving stops the money will find the slots still paying
            weeks later, because comp keys on SLOT status and this touches the
            USER row. */}
        <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          <p>
            <strong>Archiving does not stop this member&apos;s slots from earning.</strong>{' '}
            Compensation keys on slot status, not user status. If earnings should stop, suspend the
            slots separately.
          </p>
          <p className="mt-1 text-muted-foreground">
            Their purchases, certificates and position in the genealogy tree are preserved — the
            tree below them does not collapse.
          </p>
        </div>

        {blocked && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {preflight.blockers.map((b) => (
              <p key={b}>{BLOCKER_COPY[b] ?? b}</p>
            ))}
          </div>
        )}

        {decidable && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="destructive"
              disabled={blocked || execute.isPending}
              onClick={() => {
                setExecuteError(null);
                setExecuteOpen(true);
              }}
            >
              Archive account
            </Button>
            <Button
              variant="outline"
              disabled={reject.isPending}
              onClick={() => setRejectOpen(true)}
            >
              Reject request
            </Button>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={executeOpen}
        onOpenChange={setExecuteOpen}
        title={erasing ? 'Erase this account' : 'Close this account'}
        description={
          <>
            <span className="font-medium text-foreground">
              {request.memberName ?? request.memberEmail}
            </span>{' '}
            {erasing
              ? 'loses their email, phone, password, KYC and profile permanently. This cannot be undone, and the audit trail records only the field NAMES that were scrubbed.'
              : 'is signed out everywhere and can no longer sign in. Nothing is deleted, and either they or an administrator can reopen the account later.'}
          </>
        }
        confirmLabel={erasing ? 'Erase account' : 'Close account'}
        confirmVariant="destructive"
        typedConfirm={{ expected: 'ARCHIVE' }}
        loading={execute.isPending}
        errorMessage={executeError}
        onConfirm={() => execute.mutate()}
      />
      <ReviewDecisionDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject archive request"
        description="The member keeps their account. The note is stored on the request."
        label="Note (optional)"
        placeholder="Why the request was refused…"
        confirmLabel="Reject request"
        pending={reject.isPending}
        onConfirm={(note) => reject.mutate(note)}
      />
    </Card>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-medium tabular-nums">{children}</div>
    </div>
  );
}
